/**
 * AiCoachPane — Layout-level AI Coach side-pane (L&D Super-UX Phase 3 PR 3.2).
 *
 * Доступен с любой страницы через FAB в Layout.
 *
 * Endpoints: /coach/chat (conversation-aware), /coach/suggested-prompts,
 *            /coach/quota. Сохраняем conversation_id в localStorage —
 *            при reopen в течение backend idle-window (2h) — продолжаем тот же.
 *
 * UI: единые conversation/* примитивы. Drawer-вариант 640px, mobile full-screen.
 * См. .cursor/rules/conversation-ui.mdc.
 */

import { useEffect, useState } from 'react'
import {
    Brain,
    Compass,
    Heart,
    LifeBuoy,
    MessageCircleQuestion,
    Shield,
    Sparkles,
    Users,
} from 'lucide-react'

import {
    apiCoachChat,
    apiCoachSuggestedPrompts,
    apiGetCoachQuota,
} from '../../api/aiCoach'
import LoadingIcon from '../LoadingIcon'
import { COACH_FALLBACK_PROMPTS } from './coachPrompts'
import {
    Composer,
    Conversation,
    ConversationHeader,
    EmptyState,
    MessageStream,
    QuotaPill,
    SuggestionChips,
    SupportCard,
} from '../conversation'

// ── Иконки для динамических growth-подсказок из бэкенда ──────────────────────
const GROWTH_ICONS = [Compass, Brain, Users, MessageCircleQuestion, Sparkles]
function withIcons(prompts) {
    return prompts.map((text, i) => ({
        text,
        icon: GROWTH_ICONS[i % GROWTH_ICONS.length],
    }))
}

// ── Статические подсказки для эмоциональных состояний ────────────────────────
const STATE_PROMPTS = [
    { text: 'Чувствую тревогу и не могу сосредоточиться на работе', icon: Shield },
    { text: 'Чувствую выгорание — что сделать прямо сейчас?', icon: Heart },
    { text: 'Конфликт с коллегой, не знаю как справиться', icon: Users },
]

const STORAGE_KEY = 'onboardix_ai_coach_conversation_id'

function loadConvId() {
    try {
        return window.localStorage.getItem(STORAGE_KEY) || null
    } catch {
        return null
    }
}

function saveConvId(id) {
    try {
        if (id) window.localStorage.setItem(STORAGE_KEY, id)
        else window.localStorage.removeItem(STORAGE_KEY)
    } catch {
        /* ignore */
    }
}

const INTRO =
    'Спроси про работу, развитие, сложные ситуации с командой или руководителем. Коуч опирается на твою фазу, роль и базу знаний компании.'

export default function AiCoachPane({ open, onClose }) {
    const [history, setHistory] = useState([])
    const [conversationId, setConversationId] = useState(loadConvId())
    const [quota, setQuota] = useState(null)
    const [suggestedPrompts, setSuggestedPrompts] = useState([])
    const [input, setInput] = useState('')
    const [sending, setSending] = useState(false)
    const [loading, setLoading] = useState(true)
    const [toast, setToast] = useState('')
    const [supportLevel, setSupportLevel] = useState('none')

    const showToast = (msg, ms = 4000) => {
        setToast(msg)
        setTimeout(() => setToast(''), ms)
    }

    const refetchMeta = async () => {
        try {
            const [q, sp] = await Promise.all([
                apiGetCoachQuota(),
                apiCoachSuggestedPrompts(),
            ])
            setQuota(q)
            setSuggestedPrompts(sp?.prompts || [])
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('AI Coach pane meta load failed', e)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (!open) return
        setLoading(true)
        refetchMeta()
    }, [open])

    const handleSend = async (textOverride) => {
        const text = (textOverride ?? input).trim()
        if (!text || sending) return
        if (quota && !quota.enabled) {
            showToast('AI-коуч сейчас отключён. Попробуй позже.')
            return
        }
        if (quota && quota.used_today >= quota.daily_limit) {
            showToast(`Лимит на сегодня (${quota.daily_limit}) исчерпан. Попробуй завтра.`)
            return
        }
        const tmpId = `tmp-${Date.now()}`
        setHistory((h) => [
            ...h,
            { id: tmpId, role: 'user', content: text, created_at: new Date().toISOString() },
        ])
        if (!textOverride) setInput('')
        setSending(true)
        setSupportLevel('none')
        try {
            const res = await apiCoachChat({
                message: text,
                conversationId,
            })
            if (res?.conversation_id) {
                setConversationId(res.conversation_id)
                saveConvId(res.conversation_id)
            }
            setHistory((h) => [
                ...h,
                {
                    id: res?.message_id || `assistant-${Date.now()}`,
                    role: 'assistant',
                    content: res?.response_text || '',
                    created_at: new Date().toISOString(),
                },
            ])
            if (Array.isArray(res?.suggested_prompts)) {
                setSuggestedPrompts(res.suggested_prompts)
            }
            setSupportLevel(res?.support_level || 'none')
            apiGetCoachQuota().then(setQuota).catch(() => {})
        } catch (e) {
            const detail = e?.message || e?.detail || 'Не удалось отправить сообщение'
            showToast(detail.toString().slice(0, 200))
            setHistory((h) => h.filter((m) => m.id !== tmpId))
        } finally {
            setSending(false)
        }
    }

    const handleNewConversation = () => {
        saveConvId(null)
        setConversationId(null)
        setHistory([])
        setSupportLevel('none')
        showToast('Новый разговор начат.')
    }

    const quotaPill = quota ? (
        <QuotaPill
            used={quota.used_today}
            limit={quota.daily_limit}
            enabled={quota.enabled}
        />
    ) : null

    const sendBlocked =
        sending ||
        (quota && !quota.enabled) ||
        (quota && quota.used_today >= quota.daily_limit)

    // EmptyState intro — два раздела подсказок + приватная заметка
    const emptyIntro = (
        <div>
            <p className="conversation__empty-intro">{INTRO}</p>
            <p
                className="conversation__empty-intro"
                style={{ fontSize: '0.75rem', opacity: 0.65, marginTop: '-4px' }}
            >
                Коуч не делится твоими вопросами с HR или руководителем.
            </p>
            <SuggestionChips
                label="Работа и развитие"
                items={withIcons(
                    suggestedPrompts.length ? suggestedPrompts : COACH_FALLBACK_PROMPTS
                )}
                onPick={(text) => handleSend(text)}
                variant="stacked"
                disabled={sendBlocked}
            />
            <SuggestionChips
                label="Состояние"
                items={STATE_PROMPTS}
                onPick={(text) => handleSend(text)}
                variant="stacked"
                disabled={sendBlocked}
            />
        </div>
    )

    return (
        <Conversation
            open={open}
            onClose={onClose}
            variant="drawer"
            ariaLabel="AI-коуч развития"
        >
            <ConversationHeader
                icon={Sparkles}
                title="AI-коуч развития"
                subtitle={quotaPill}
                actions={[
                    {
                        key: 'support',
                        icon: LifeBuoy,
                        label: 'Ресурсы поддержки',
                        title: 'Ресурсы поддержки',
                        onClick: () =>
                            setSupportLevel((l) => (l === 'none' ? 'support' : 'none')),
                    },
                    {
                        key: 'new',
                        icon: Sparkles,
                        label: 'Новый разговор',
                        title: 'Новый разговор',
                        onClick: handleNewConversation,
                    },
                ]}
                onClose={onClose}
            />

            <MessageStream
                messages={history}
                sending={sending}
                loading={loading}
                collapsibleAssistant
                loadingSlot={
                    <div style={{ padding: '40px 0', textAlign: 'center' }}>
                        <LoadingIcon />
                    </div>
                }
                emptySlot={
                    <EmptyState
                        heroIcon={Sparkles}
                        heading="Чем могу помочь сегодня?"
                        intro={emptyIntro}
                    />
                }
            />

            {supportLevel !== 'none' ? (
                <SupportCard
                    level={supportLevel}
                    onDismiss={() => setSupportLevel('none')}
                />
            ) : null}

            <Composer
                value={input}
                onChange={setInput}
                onSubmit={(text) => handleSend(text)}
                placeholder="Задай вопрос коучу…"
                disabled={sendBlocked}
                submitOnEnter={false}
                showVoice
                hint="Enter — перенос строки · Ctrl/Cmd + Enter — отправить"
                autoFocus={open}
            />

            {toast ? <div className="conversation__toast">{toast}</div> : null}
        </Conversation>
    )
}
